import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

type WorkforceTool = 'command' | 'calendar' | 'timesheet' | 'map' | 'fleet';

@Component({
  selector: 'app-workforce-operations-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <section class="bg-white border border-[#E7EEF5] rounded-lg mb-4 overflow-hidden">
      <div class="px-3 py-2 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-2">
        <div class="min-w-0">
          <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6]">Workforce Operations</p>
          <p class="text-[11px] font-semibold text-slate-700 truncate">{{ activeSummary() }}</p>
        </div>
        <div class="flex flex-wrap gap-1.5">
          @for (tool of tools; track tool.key) {
            <a
              [routerLink]="tool.route"
              routerLinkActive
              #rla="routerLinkActive"
              [routerLinkActiveOptions]="{ exact: true }"
              class="h-8 px-2.5 rounded-md border text-[10px] font-bold flex items-center gap-1.5 transition"
              [ngClass]="isSelected(tool.key, rla.isActive) ? 'bg-[#0C3A5A] border-[#0C3A5A] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-200 hover:text-[#1A6FA6]'"
              [attr.aria-current]="isSelected(tool.key, rla.isActive) ? 'page' : null"
            >
              <i class="ti text-[14px]" [class]="'ti-' + tool.icon"></i>
              <span>{{ tool.label }}</span>
            </a>
          }
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        @for (tool of tools; track tool.key) {
          <a
            [routerLink]="tool.route"
            class="px-3 py-2 flex items-start gap-2 transition hover:bg-slate-50"
            [ngClass]="active === tool.key ? 'bg-sky-50/70' : 'bg-white'"
          >
            <span class="mt-0.5 w-7 h-7 rounded-md flex items-center justify-center shrink-0" [ngClass]="active === tool.key ? 'bg-[#0C3A5A] text-white' : 'bg-slate-50 text-slate-500'">
              <i class="ti text-[15px]" [class]="'ti-' + tool.icon"></i>
            </span>
            <span class="min-w-0">
              <span class="block text-[11px] font-bold text-slate-900 truncate">{{ tool.title }}</span>
              <span class="block text-[10px] text-slate-500 leading-snug">{{ tool.outcome }}</span>
            </span>
          </a>
        }
      </div>
    </section>
  `
})
export class WorkforceOperationsNavComponent {
  @Input() active: WorkforceTool = 'command';

  tools: Array<{ key: WorkforceTool; label: string; title: string; outcome: string; route: string; icon: string }> = [
    { key: 'command', label: 'Command', title: 'Operations Command', outcome: 'Queue, risk, cash, and workload.', route: '/admin/operations/report', icon: 'chart-dots-3' },
    { key: 'calendar', label: 'Calendar', title: 'Coverage Calendar', outcome: 'Who is booked, open, or away.', route: '/admin/operations/calendar', icon: 'calendar-week' },
    { key: 'timesheet', label: 'Timesheet', title: 'Timesheet Control', outcome: 'Worked hours, paid hours, breaks.', route: '/admin/operations/timesheet', icon: 'clock-hour-4' },
    { key: 'map', label: 'Workers', title: 'Workers Control', outcome: 'App health, live map, GPS gaps.', route: '/admin/operations/workers-control', icon: 'device-mobile-check' },
    { key: 'fleet', label: 'Fleet', title: 'Fleet Status', outcome: 'Assignments, WOF/RUC, service risk.', route: '/admin/operations/fleet', icon: 'truck' }
  ];

  activeSummary(): string {
    return this.tools.find(tool => tool.key === this.active)?.outcome || 'Select an operations tool.';
  }

  isSelected(key: WorkforceTool, routeActive: boolean): boolean {
    return this.active === key || routeActive;
  }
}
